import { useState } from 'react';
import toast from 'react-hot-toast';
import { Portal } from './Portal';

interface GozapMobileConnectModalProps {
  sessionName: string;
  displayName: string;
  initialPhone?: string;
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
  onClose: () => void;
  onConnected: () => void;
}

type Step = 'phone' | 'method' | 'code' | 'pin';

export function GozapMobileConnectModal({ sessionName, displayName, initialPhone, authenticatedFetch, onClose, onConnected }: GozapMobileConnectModalProps) {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState(initialPhone || '');
  const [availableMethods, setAvailableMethods] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);

  const base = `/api/waha/sessions/${encodeURIComponent(sessionName)}/gozap/mobile`;

  const handleCheckOptions = async () => {
    if (!phone.trim()) {
      toast.error('Digite o telefone com DDI (ex: 5511999999999)');
      return;
    }
    setLoading(true);
    try {
      const resp = await authenticatedFetch(`${base}/verification-options`, {
        method: 'POST',
        body: JSON.stringify({ phone: phone.trim() })
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.error || 'Erro ao consultar opções de verificação');
      }

      const methods: string[] = (data.verificationOptions?.methods || [])
        .filter((m: any) => m?.available !== false && (m?.id === 'sms' || m?.id === 'call'))
        .map((m: any) => m.id);
      setAvailableMethods(methods.length > 0 ? methods : ['sms', 'call']);
      setStep('method');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao consultar opções de verificação');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async (method: 'sms' | 'voice') => {
    setLoading(true);
    try {
      const resp = await authenticatedFetch(`${base}/request-code`, {
        method: 'POST',
        body: JSON.stringify({ phone: phone.trim(), method: method === 'voice' ? 'call' : 'sms', name: displayName })
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.error || 'Erro ao solicitar código');
      }
      toast.success(method === 'voice' ? 'Ligação solicitada. Aguarde a chamada.' : 'Código enviado por SMS');
      setStep('code');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao solicitar código');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code.trim()) {
      toast.error('Digite o código recebido');
      return;
    }
    setLoading(true);
    try {
      const resp = await authenticatedFetch(`${base}/verify-code`, {
        method: 'POST',
        body: JSON.stringify({ phone: phone.trim(), code: code.trim() })
      });
      const data = await resp.json();
      if (!resp.ok) {
        const message = data.error || '';
        if (/2fa|pin|duas etapas/i.test(message)) {
          setStep('pin');
          return;
        }
        throw new Error(message || 'Código inválido');
      }
      toast.success('Código confirmado! Sincronizando conta...');
      onConnected();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao confirmar código');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPin = async () => {
    if (!pin.trim()) {
      toast.error('Digite o PIN de verificação em duas etapas');
      return;
    }
    setLoading(true);
    try {
      const resp = await authenticatedFetch(`${base}/two-factor`, {
        method: 'POST',
        body: JSON.stringify({ phone: phone.trim(), code: code.trim(), pin: pin.trim() })
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.error || 'PIN inválido');
      }
      toast.success('PIN confirmado! Sincronizando conta...');
      onConnected();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao confirmar PIN');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Portal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center backdrop-blur-sm" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md border border-gray-100 m-4" role="dialog" aria-labelledby="gozap-mobile-title">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 id="gozap-mobile-title" className="text-xl font-bold text-gray-900">
                Conectar Tavola Mobile
              </h2>
              <p className="text-sm text-gray-500 mt-1">{displayName}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {step === 'phone' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="gozap-phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Número do WhatsApp (com DDI)
                </label>
                <input
                  id="gozap-phone"
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Ex: 5511999999999"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  disabled={loading}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-2">
                  Este número não precisa ter WhatsApp instalado no celular — a conexão é feita diretamente pela API.
                </p>
              </div>
              <button
                onClick={handleCheckOptions}
                disabled={loading || !phone.trim()}
                className="w-full bg-green-600 text-white py-3 px-6 rounded-xl hover:bg-green-700 font-medium disabled:opacity-50"
              >
                {loading ? 'Consultando...' : 'Consultar opções de verificação'}
              </button>
            </div>
          )}

          {step === 'method' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Escolha como deseja receber o código de verificação:</p>
              <div className="flex gap-3">
                {availableMethods.includes('sms') && (
                  <button
                    onClick={() => handleRequestCode('sms')}
                    disabled={loading}
                    className="flex-1 bg-green-600 text-white py-3 px-4 rounded-xl hover:bg-green-700 font-medium disabled:opacity-50"
                  >
                    SMS
                  </button>
                )}
                {(availableMethods.includes('voice') || availableMethods.includes('call')) && (
                  <button
                    onClick={() => handleRequestCode('voice')}
                    disabled={loading}
                    className="flex-1 bg-gray-700 text-white py-3 px-4 rounded-xl hover:bg-gray-800 font-medium disabled:opacity-50"
                  >
                    Ligação
                  </button>
                )}
              </div>
              <button onClick={() => setStep('phone')} className="text-sm text-gray-500 hover:text-gray-700">
                ← Voltar
              </button>
            </div>
          )}

          {step === 'code' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="gozap-code" className="block text-sm font-semibold text-gray-700 mb-2">
                  Código recebido
                </label>
                <input
                  id="gozap-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Digite o código"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  disabled={loading}
                  autoFocus
                />
              </div>
              <button
                onClick={handleVerifyCode}
                disabled={loading || !code.trim()}
                className="w-full bg-green-600 text-white py-3 px-6 rounded-xl hover:bg-green-700 font-medium disabled:opacity-50"
              >
                {loading ? 'Confirmando...' : 'Confirmar código'}
              </button>
              <button onClick={() => setStep('method')} className="text-sm text-gray-500 hover:text-gray-700">
                ← Voltar
              </button>
            </div>
          )}

          {step === 'pin' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Esta conta possui verificação em duas etapas ativada. Digite o PIN configurado no WhatsApp.
              </p>
              <div>
                <label htmlFor="gozap-pin" className="block text-sm font-semibold text-gray-700 mb-2">
                  PIN (verificação em duas etapas)
                </label>
                <input
                  id="gozap-pin"
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  disabled={loading}
                  autoFocus
                />
              </div>
              <button
                onClick={handleConfirmPin}
                disabled={loading || !pin.trim()}
                className="w-full bg-green-600 text-white py-3 px-6 rounded-xl hover:bg-green-700 font-medium disabled:opacity-50"
              >
                {loading ? 'Confirmando...' : 'Confirmar PIN'}
              </button>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}
